import { useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';

import { BackendRoute } from '../../config/index';
import { QueryKey } from '../../constants/index';
import { getUnauthorizedRedirect } from '../../guards/page-access.util';
import { ApiService } from '../../services/index';
import { selectedCompanyStorage } from '../../storage/company/index';
import { PageRoute } from '../../types/page.types';
import { Snapshot } from '../../types/snapshot.types';
import { useAppQuery } from '../use-app-query.hook';

export const useCurrent = ({
  capTableMode,
  companyId,
  enabled = true,
  includePhantom,
}: {
  capTableMode?: boolean;
  companyId: string;
  enabled?: boolean;
  includePhantom?: boolean;
}) => {
  const queryClient = useQueryClient();
  const company = selectedCompanyStorage(({ company }) => company);
  const canReadSnapshot =
    company != null && getUnauthorizedRedirect(PageRoute.CAP_TABLE, company) === null;

  const {
    data: snapshot,
    isLoading,
    isSuccess,
  } = useAppQuery<Snapshot>({
    queryKey: [QueryKey.GET_SNAPSHOT, companyId, includePhantom, capTableMode],
    queryFn: () =>
      ApiService.get(
        {
          endpoint: BackendRoute.COMPANIES,
          routePath: [companyId, capTableMode ? 'cap-table-current-snapshot' : 'current-snapshot'],
        },
        {
          queryParams: {
            includePhantom,
          },
        },
      ),
    enabled: enabled && !!companyId && canReadSnapshot,
    staleTime: 0,
    gcTime: 0,
  });

  const invalidateQuery = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: [QueryKey.GET_SNAPSHOT] });
  }, [queryClient]);

  return {
    invalidateQuery,
    snapshot,
    isLoading,
    isSuccess,
  };
};
